import { useEffect, useRef } from "react";

/**
 * The hero constellation: drifting nodes that lean toward the pointer and draw
 * links to their neighbours.
 *
 * Ported from the `prod` branch's ParticleCanvas. The physics are unchanged —
 * a 200px attraction radius at 0.04 force, a 0.003 spring back to each node's
 * origin, 0.97 damping, links faded by distance out to 120px, and a soft glow
 * under the pointer. Four things had to change to live here:
 *
 *   1. Theme comes in as a prop. The original sniffed
 *      `document.documentElement.classList.contains("dark")` through a
 *      MutationObserver, which this design does not use — it swaps CSS custom
 *      properties on its own root instead, so the class never changes and the
 *      effect would have been permanently stuck in one theme.
 *   2. The palette is the canvas's. The original cycled five hues including
 *      indigo and purple, which read as a different product against warm paper.
 *      Nodes and links are drawn from the one accent, lighter or darker per
 *      theme, so it belongs to the page in both.
 *   3. Pointer events stay off the canvas. The original bound mousemove to the
 *      canvas itself, which means the canvas has to receive clicks — over this
 *      hero that would swallow the two call-to-action buttons. It listens on
 *      the hero instead and stays `pointerEvents: none`, which also matches the
 *      brief: the effect answers to the pointer anywhere in the hero.
 *   4. The device-pixel-ratio scale is reset per resize. The original called
 *      `ctx.scale(dpr, dpr)` on every resize without clearing the previous
 *      transform, so each resize multiplied the scale again and the field
 *      drifted off-canvas. `setTransform` replaces rather than compounds.
 *
 * Honours prefers-reduced-motion by painting one static frame: the shape of the
 * constellation survives, the drift and the pointer response do not.
 */

interface ConstellationProps {
  dark: boolean;
}

interface Node {
  x: number;
  y: number;
  baseX: number;
  baseY: number;
  vx: number;
  vy: number;
  radius: number;
  opacity: number;
  pulse: number;
  pulseSpeed: number;
  /** Index into the active theme's node palette. */
  tone: number;
}

const ATTRACTION_RADIUS = 200;
const ATTRACTION_FORCE = 0.04;
const CONNECTION_DIST = 120;
const MAX_NODES = 140;

/** Node fills, as "r, g, b" so alpha can be varied per frame. */
const NODE_TONES = {
  dark: ["56, 229, 255", "157, 241, 255"],
  light: ["10, 166, 192", "8, 117, 133"],
};
const LINE_RGB = { dark: "56, 229, 255", light: "8, 117, 133" };
/** Cyan on cream is far weaker than cyan on near-black; light needs the lift. */
const OPACITY_MULT = { dark: 1, light: 1.9 };
/**
 * Resting link opacity. Higher than prod's 0.15/0.32 on purpose: prod draws
 * over a near-plain ground, while this hero already carries a starfield, two
 * rings and a perspective grid. At prod's values the links were measurably
 * being drawn here and still read as invisible.
 */
const LINE_ALPHA = { dark: 0.34, light: 0.4 };
/** How much brighter a link gets when the pointer is on top of it. */
const PROXIMITY_BOOST = 1.8;
/** Opacity of the links drawn from the pointer itself to nearby nodes. */
const CURSOR_LINK_ALPHA = { dark: 0.5, light: 0.55 };

export default function Constellation({ dark }: ConstellationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Read inside the frame loop so a theme change repaints without tearing the
  // field down and rebuilding it — the nodes keep their positions.
  const darkRef = useRef(dark);
  darkRef.current = dark;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const host = canvas.closest("#hero") as HTMLElement | null;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let nodes: Node[] = [];
    let w = 0;
    let h = 0;
    const mouse = { x: -9999, y: -9999 };

    const build = () => {
      const count = Math.min(MAX_NODES, Math.floor((w * h) / 8000));
      nodes = Array.from({ length: count }, () => {
        const x = Math.random() * w;
        const y = Math.random() * h;
        return {
          x,
          y,
          baseX: x,
          baseY: y,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.3,
          radius: Math.random() * 2 + 1,
          opacity: Math.random() * 0.5 + 0.2,
          pulse: Math.random() * Math.PI * 2,
          pulseSpeed: Math.random() * 0.02 + 0.01,
          tone: Math.random() < 0.5 ? 0 : 1,
        };
      });
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      // setTransform, not scale: scale multiplies onto whatever is already
      // there, so repeated resizes would compound.
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      w = rect.width;
      h = rect.height;
      build();
    };

    const paint = (animate: boolean) => {
      const theme = darkRef.current ? "dark" : "light";
      const tones = NODE_TONES[theme];
      const opacityMult = OPACITY_MULT[theme];

      ctx.clearRect(0, 0, w, h);

      for (const p of nodes) {
        if (animate) {
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const dist = Math.hypot(dx, dy);
          if (dist < ATTRACTION_RADIUS && dist > 0) {
            const force = (1 - dist / ATTRACTION_RADIUS) * ATTRACTION_FORCE;
            p.vx += (dx / dist) * force;
            p.vy += (dy / dist) * force;
          }
          // Spring home, so the field recovers its shape once the pointer goes.
          p.vx += (p.baseX - p.x) * 0.003;
          p.vy += (p.baseY - p.y) * 0.003;
          p.vx *= 0.97;
          p.vy *= 0.97;
          p.x += p.vx;
          p.y += p.vy;
          p.pulse += p.pulseSpeed;
        }

        const pulseScale = 1 + Math.sin(p.pulse) * 0.3;
        const r = p.radius * pulseScale;
        const alpha = p.opacity * (0.7 + Math.sin(p.pulse) * 0.3) * opacityMult;
        const rgb = tones[p.tone];

        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${rgb}, ${alpha})`;
        ctx.fill();

        const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r * 3);
        glow.addColorStop(0, `rgba(${rgb}, ${alpha * 0.3})`);
        glow.addColorStop(1, `rgba(${rgb}, 0)`);
        ctx.beginPath();
        ctx.arc(p.x, p.y, r * 3, 0, Math.PI * 2);
        ctx.fillStyle = glow;
        ctx.fill();
      }

      // Links. O(n^2) over at most 140 nodes, which is the same budget the
      // original ran on and stays inside a frame at 60fps.
      ctx.lineWidth = 0.5;
      const lineRgb = LINE_RGB[theme];
      const maxAlpha = LINE_ALPHA[theme];
      const pointerOn = animate && mouse.x > -9000;

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const n1 = nodes[i];
          const n2 = nodes[j];
          const dist = Math.hypot(n1.x - n2.x, n1.y - n2.y);
          if (dist >= CONNECTION_DIST) continue;

          let alpha = (1 - dist / CONNECTION_DIST) * maxAlpha;
          // Links under the pointer brighten. Attraction alone moves the nodes
          // closer, which thickens the web, but the change is easy to miss
          // against a busy hero — this is what makes hovering legible as
          // "the nodes are connecting".
          if (pointerOn) {
            const midDist = Math.hypot(
              mouse.x - (n1.x + n2.x) / 2,
              mouse.y - (n1.y + n2.y) / 2,
            );
            if (midDist < ATTRACTION_RADIUS) {
              alpha *= 1 + (1 - midDist / ATTRACTION_RADIUS) * PROXIMITY_BOOST;
            }
          }
          ctx.beginPath();
          ctx.moveTo(n1.x, n1.y);
          ctx.lineTo(n2.x, n2.y);
          ctx.strokeStyle = `rgba(${lineRgb}, ${Math.min(alpha, 1)})`;
          ctx.stroke();
        }
      }

      if (pointerOn) {
        // The pointer joins the constellation: it links to every node inside
        // the attraction radius, so the web visibly reaches for the cursor
        // rather than only drifting toward it.
        for (const p of nodes) {
          const d = Math.hypot(mouse.x - p.x, mouse.y - p.y);
          if (d >= ATTRACTION_RADIUS) continue;
          ctx.beginPath();
          ctx.moveTo(mouse.x, mouse.y);
          ctx.lineTo(p.x, p.y);
          ctx.strokeStyle = `rgba(${lineRgb}, ${(1 - d / ATTRACTION_RADIUS) * CURSOR_LINK_ALPHA[theme]})`;
          ctx.stroke();
        }

        const g = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, 150);
        g.addColorStop(0, `rgba(${lineRgb}, ${darkRef.current ? 0.1 : 0.08})`);
        g.addColorStop(1, `rgba(${lineRgb}, 0)`);
        ctx.fillStyle = g;
        ctx.fillRect(mouse.x - 150, mouse.y - 150, 300, 300);
      }
    };

    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    if (reduce) {
      paint(false);
      return () => ro.disconnect();
    }

    const onMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };
    const onLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };
    host?.addEventListener("mousemove", onMove);
    host?.addEventListener("mouseleave", onLeave);

    let raf = 0;
    const loop = () => {
      paint(true);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      host?.removeEventListener("mousemove", onMove);
      host?.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        // Decoration only: the hero's buttons sit above this and must stay
        // clickable, and the pointer is tracked on the hero regardless.
        pointerEvents: "none",
        zIndex: 2,
      }}
    />
  );
}
